import { GetStaticPropsContext } from "next";
import { useTranslation } from "next-i18next";

import { useRouter } from "next/router";

import React, { useContext, useEffect, useMemo } from "react";

import imageUrlBuilder from "@sanity/image-url";
// eslint-disable-next-line import/no-unresolved
import { SanityImageSource } from "@sanity/image-url/lib/types/types";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

import Layout from "../../components/Layout";
import PageTitle from "../../components/Molecules/PageTitle";
import SEO from "../../components/Molecules/SEO";
import AlternateLangContext, {
    AlternateLangUrl
} from "../../context/alternateLangContext/context";
import Articles, { PostProps } from "../../features/articles";
import { ImageBlock } from "../../lib/sanity/types";
import sanityClient from "../../lib/sanityClient";

interface Props extends GetStaticPropsContext {
    params: {
        slug: string;
    };
}

const builder = imageUrlBuilder(sanityClient);

type ArticleProps = PostProps;

function urlFor(source: SanityImageSource) {
    return builder.image(source);
}

const ArticlePage = ({ post, alternateContent }: ArticleProps) => {
    const router = useRouter();
    const { locale } = router;

    const { t } = useTranslation(["article", "common"]);

    const { setAlternateUrls } = useContext(AlternateLangContext);

    const altUrls: AlternateLangUrl[] = useMemo(
        () =>
            alternateContent.map((link) => ({
                href: link.href,
                hrefLang: link.hrefLang
            })),
        [alternateContent]
    );

    useEffect(() => {
        setAlternateUrls(altUrls);
    }, [altUrls, setAlternateUrls]);

    if (!post) {
        return <div>Error</div>;
    }

    const { publishedAt, title } = post;

    const date = t("common:dateFormatted", {
        val: new Date(publishedAt),
        formatParams: {
            val: { weekday: "long", year: "numeric", month: "long", day: "numeric" }
        }
    });

    if (!router.isFallback && !post.slug.current) {
        return <div>Error</div>;
    }

    const images = post.body
        // eslint-disable-next-line no-underscore-dangle
        .filter((block): block is ImageBlock => block._type === "image")
        .map(({ alt, asset }) => ({
            alt,
            url: urlFor(asset).width(1200).toString()
        }));

    const alternateLinks: AlternateLangUrl[] = alternateContent
        .filter((alt) => !alt.fallback)
        .map((link) => ({
            href: link.href,
            hrefLang: link.hrefLang
        }));

    return (
        <>
            <SEO
                title={post.seo?.metaTitle || post.title}
                description={post.seo?.metaDescription || post.excerpt}
                openGraph={{
                    title: post.seo?.metaTitle || post.title,
                    description: post.seo?.metaDescription || post.excerpt,
                    type: "article",
                    images: images || undefined,
                    article: {
                        publishedTime: post.publishedAt,
                        tags: post.tags?.map((tag) => tag.name) || undefined,
                        authors: ["Carl Topham"],
                        section: "Articles"
                    }
                }}
                canonical={post.canonical}
                // Remove current language from alternate links
                alternateLinks={alternateLinks}
            />

            <PageTitle title={title} subTitle={date} />

            <div>
                <Articles.Post
                    post={post}
                    alternateContent={alternateContent}
                    locale={locale}
                />
            </div>
        </>
    );
};

ArticlePage.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>;

export default ArticlePage;

export async function getStaticProps(context: Props) {
    const {
        locale,
        params: { slug },
        preview = false
    } = context;

    return Articles.getArticleDetails(slug, locale, preview)
        .then(async (post) => {
            if (!post) {
                return {
                    notFound: true,
                    revalidate: 10
                };
            }
            const alternateContent = [
                { href: "/articles/page/1", hrefLang: "en", fallback: true },
                { href: "/fr/articles/page/1", hrefLang: "fr", fallback: true }
            ].map((fallback) => {
                const translatedContent = post.translatedContent?.translations?.find(
                    (translation) => translation.value?.hrefLang === fallback.hrefLang
                );

                if (translatedContent) {
                    return {
                        href: translatedContent.value?.href,
                        hrefLang: translatedContent.value?.hrefLang,
                        fallback: false
                    };
                }

                return fallback;
            });

            return {
                props: {
                    post,
                    alternateContent,
                    ...(await serverSideTranslations(locale || "en", [
                        "common",
                        "article",
                        "nav"
                    ]))
                }
            };
        })
        .catch((e) => {
            // eslint-disable-next-line no-console
            console.error(e);
            return {
                notFound: true,
                revalidate: 10
            };
        });
}

type LocaleOption = "en" | "fr";

type LocalisedPath = {
    params: {
        slug: string;
    };
    locale: LocaleOption;
};

export async function getStaticPaths() {
    return Articles.getPostPaths()
        .then((data) => {
            const paths: LocalisedPath[] = data.map(({ slug, language }) => ({
                params: { slug: slug.current },
                locale: language
            }));

            return {
                paths,
                fallback: "blocking" // If missing then attempt to render page
            };
        })
        .catch((error) => {
            // eslint-disable-next-line no-console
            console.log(JSON.stringify(error, null, 2));
            return {
                notFound: true,
                revalidate: 10
            };
        });
}
